//@flow
import RiderPersonaActions from 'common/redux/actions/riderPersona';

import { getClientDimensionsMapping } from 'common/redux/selectors/clientSelector';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';

import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import {
    RiderPersona as MasterRiderPersona,
    RiderPersonaTypeEnum,
    FleetTypeEnum
} from 'common/models/internal/MasterRiderPersona';

import { Skill } from 'common/models/internal/Skill';

import {
    riderPersona as riderPersonaApi,
    skill as skillsApi
} from 'common/middleware/api';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import RiderPersonaLimit from './RiderPersonaLimits';
import TransporterIdInput from './TransporterIdInput';
import commonTestIds from 'common/tests/entity/entityIds.json';
import riderPersonaMasterTestIds from 'common/tests/entity/riderPersonaMasterIds.json';
import CustomColumnHelpers from 'common/entity/helpers/CustomColumnHelpers';

import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import DataUtils from 'common/utils/DataUtils';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { volumeUnitOptions } from 'common/enums/VolumeUnitEnum';
import type { TFunction } from 'react-i18next';
import type { EntitySettings } from 'common/models/internal/EntitySettings';

type GetBooleanExtraPropsInput = {
    field: { value: boolean },
    t: TFunction,
    isEditing: ?string,
    entitySettings: ?EntitySettings
};

type GetBooleanExtraPropsOutput = { label: string };

const getBooleanExtraProps = ({
    field: { value, defaultValue },
    t,
    entitySettings,
    isEditing
}: GetBooleanExtraPropsInput): GetBooleanExtraPropsOutput => {
    if (entitySettings && arePjpFieldsDisabled(entitySettings) && !isEditing) {
        return {
            label: defaultValue
                ? t('riderPersonaForm.boolean.yes')
                : t('riderPersonaForm.boolean.no')
        };
    }
    return {
        label: value
            ? t('riderPersonaForm.boolean.yes')
            : t('riderPersonaForm.boolean.no')
    };
};

const transporterIdInputFilters = ({ input }) => {
    let filters = new FilterListBuilder().addAll([
        new FilterBuilder()
            .withName('status')
            .withOperation(FilterOperation.Equals)
            .withValue('ACTIVE')
    ]);

    if (!DataUtils.isEmpty(input)) {
        filters = filters.add(
            new FilterBuilder()
                .withName('id')
                .withOperation(FilterOperation.ContainsToken)
                .withValue(input)
        );
    }
    return filters.build();
};

const RiderPersonaTypeOptions = RiderPersonaTypeEnum.getOptions({
    translationKey: 'masterRiderPersonaProperties.riderPersonaType'
});

const FleetTypeOptions = FleetTypeEnum.getOptions({
    translationKey: 'masterRiderPersonaProperties.fleetTypes'
});

const arePjpFieldsDisabled = entitySettings => {
    return !entitySettings.pjpSettings.enabled;
};

const RIDERPERSONA_FIELDS = [
    {
        accessor: 'code',
        label: 'riderPersonaForm.code',
        helpText: 'riderPersonaForm.codeTooltip',
        halfWidth: false,
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.get('id'),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            } else if ('code' in value) {
                return entity.setCode(code);
            }
        },
        testId: commonTestIds.inputs.code
    },
    {
        accessor: 'name',
        label: 'riderPersonaForm.name',
        placeholder: 'riderPersonaForm.namePlaceholder',
        required: true,
        type: FieldTypeEnum.Text,
        testId: commonTestIds.inputs.name
    },
    {
        accessor: 'transporterId',
        label: 'riderPersonaForm.transporterId',
        placeholder: 'riderPersonaForm.transporterIdPlaceholder',
        required: true,
        helptext: 'riderPersonaForm.transporterIdTooltip',
        component: TransporterIdInput,
        onChangeHandler: ({ entity, field, value }) =>
            entity.setIn(field.absoluteAccessor, value),
        extraProps: ({ entity, field }) => {
            return {
                popOverContent: field.validationMessage,
                isPopOverOpen: !!field.validationMessage,
                transporter: entity.getIn(field.absoluteAccessor)
                    ?.transporterId,
                getTransporterFilters: input =>
                    transporterIdInputFilters({ input })
            };
        }
    },
    {
        accessor: 'type',
        type: FieldTypeEnum.Select,
        label: 'riderPersonaForm.type',
        placeholder: 'riderPersonaForm.typePlaceholder',
        required: ({ props, entity }) =>
            !arePjpFieldsDisabled(props.entitySettings) || entity.enablePjp,
        options: RiderPersonaTypeOptions,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden()
    },
    {
        accessor: 'fleetType',
        type: FieldTypeEnum.Select,
        label: 'riderPersonaForm.fleetType',
        placeholder: 'riderPersonaForm.fleetTypePlaceholder',
        required: true,
        options: FleetTypeOptions,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value?.value);
        }
    },
    {
        accessor: 'allowMultiTrip',
        label: 'riderPersonaForm.allowMultiTrip',
        placeholder: 'riderPersonaForm.allowMultiTripPlaceholder',
        required: ({ props }) => !arePjpFieldsDisabled(props.entitySettings),
        type: FieldTypeEnum.Boolean,
        extraProps: ({ field, t, isEditing, props: { entitySettings } }) => {
            return {
                ...getBooleanExtraProps({
                    field,
                    t,
                    entitySettings,
                    isEditing
                }),
                disabled: arePjpFieldsDisabled(entitySettings)
            };
        },
        defaultValue: false
    },
    {
        accessor: 'enableAuth',
        label: 'riderPersonaForm.enableAuth',
        type: FieldTypeEnum.Boolean,
        dependentAccessors: ['enableLotr'],
        onChangeHandler: ({ entity, value }) => {
            if (value) {
                return entity.setEnableLotr(value).setEnableAuth(value);
            }
            return entity.setEnableAuth(value);
        },
        extraProps: ({ field, t, isEditing, props: { entitySettings } }) => {
            return {
                ...getBooleanExtraProps({
                    field,
                    t,
                    entitySettings,
                    isEditing
                }),
                disabled: arePjpFieldsDisabled(entitySettings)
            };
        },
        defaultValue: true
    },
    {
        accessor: 'enableLotr',
        label: 'riderPersonaForm.enableLotr',
        type: FieldTypeEnum.Boolean,
        extraProps: ({
            entity,
            props: { entitySettings },
            isEditing,
            field,
            t
        }) => {
            return {
                ...getBooleanExtraProps({
                    field,
                    t,
                    entitySettings,
                    isEditing
                }),
                disabled:
                    arePjpFieldsDisabled(entitySettings) || entity.enableAuth
            };
        },
        defaultValue: true
    },
    {
        accessor: 'enablePjp',
        label: 'riderPersonaForm.enablePjp',
        type: FieldTypeEnum.Boolean,
        extraProps: ({ props: { entitySettings }, isEditing, field, t }) => {
            return getBooleanExtraProps({
                field,
                t,
                entitySettings,
                isEditing
            });
        },
        isHidden: ({ isFieldHidden }) => isFieldHidden()
    },
    {
        accessor: 'skills',
        type: FieldTypeEnum.Skills,
        options: ({ field }) => {
            const skills = field.fetchInfo.response?.skills || [];
            return skills && skills.map(skill => Skill.fromJS(skill));
        },
        extraProps: ({ field, t }) => {
            return {
                translationsObject: {
                    label: t('riderPersonaForm.skills'),
                    placeholder: t('riderPersonaForm.skillSelectPlaceholder'),
                    addSkillsText: t('riderPersonaForm.addSkill')
                },
                isLoading: !field.fetchInfo.response
            };
        },
        fetchInfo: {
            fetcher: skillsApi.fetch.bind(skillsApi),
            params: ({ props }) => {
                return { clientId: props.clientId, team: [] };
            },
            response: null
        },
        testId: riderPersonaMasterTestIds.inputs.skills
    },
    {
        accessor: 'volumeUnit',
        type: FieldTypeEnum.Select,
        label: 'riderPersonaForm.volumeUnit',
        helpText: 'riderPersonaForm.volumeUnitTooltip',
        options: volumeUnitOptions,
        dependentAccessors: ['limits'],
        onChangeHandler: ({ entity, field, value }) => {
            const updatedEntity = entity.setIn(
                field.absoluteAccessor,
                value?.value
            );
            if (entity.limits?.volume) {
                return updatedEntity.setIn(
                    ['limits', 'volume', 'unit'],
                    value?.value
                );
            }
            return updatedEntity;
        },
        extraProps: ({ entity }) => {
            return {
                value: entity.limits?.volume?.unit || entity.volumeUnit
            };
        }
    },
    {
        label: 'riderPersonaForm.limits',
        accessor: 'limits',
        component: RiderPersonaLimit,
        onChangeHandler: ({ entity, field, value }) => {
            return entity.setIn(field.absoluteAccessor, value);
        },
        extraProps: ({ t, entity, props }) => {
            const selectedUnit = {
                volume: entity.volumeUnit
            };
            const customFields = CustomColumnHelpers.getCustomFields(
                props.entitySettings,
                'riderPersonaSettings'
            );
            return {
                t,
                limits: entity
                    .get('limits')
                    ?.convertToPreferredUnit?.(
                        props.unitConverter,
                        selectedUnit
                    ),
                unitConverter: props.unitConverter,
                selectedUnit,
                customFields
            };
        }
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'riderPersonaSettings',
                skipResources: true
            };
        }
    }
];

const RIDERPERSONA_INITIAL_FETCH_FIELDS = ['skills'];

const SingleRiderPersonaEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterRiderPersona,
    MasterActions: RiderPersonaActions,
    EntityFetchAPI: riderPersonaApi,
    INITIAL_FETCH_FIELDS: RIDERPERSONA_INITIAL_FETCH_FIELDS,
    FIELDS: RIDERPERSONA_FIELDS,
    options: {
        ENTITY_NAME_ID: 'masterRiderPersona',
        PARAM_ID: 'riderPersonaId',
        ENTITY_SETTINGS_NAME: 'riderPersonaSettings'
    },
    extraApiParams: {
        include: IncludeTypeEnums.Transporter
    },
    mapEntityStateToProps: ({ state, props }) => {
        const dimensionsMapping = getClientDimensionsMapping(state, props);

        let unitConverter = null;
        if (dimensionsMapping) {
            unitConverter = new UnitConversionHelper({
                dimensionsMapping
            });
        }

        return {
            unitConverter
        };
    }
});

export default SingleRiderPersonaEditContainer;
